import Emittery from 'emittery';
import { getSerializedCssSizeValue } from '@mwkt/dom-utils';
import { Status, ERROR_INITIALIZE_FAILED, UNINITIALIZED_STATUSES, HANDSHAKE_START_EVENT, HANDSHAKE_REPLY_EVENT, WIZARD_EVENT_KIND, ERROR_EMIT_FAILED, INNER_ROOT_SET_HEIGHT_EVENT } from '../constants.js';
import { isHandshakeEvent } from '../utils/is-handshake-event.js';
import { isValidEvent } from '../utils/is-valid-event.js';
import { createFrameInnerEvent } from '../events.js';
import { createState } from '../utils/create-state.js';
import { debugLog } from '../utils/debug-log.js';

const DEFAULT_ALLOWED_ORIGIN = "*";
const INITIAL_STATE = {
  status: Status.Idle,
  errorCode: null,
  height: null
};
class FrameInner extends Emittery {
  inner;
  wizard;
  resizeObserver;
  allowedOrigin;
  rootDomElement;
  parentOrigin;
  state;
  onStatusChange;
  debugMode;
  constructor(options = {}) {
    super();
    const { rootSelector, allowedOrigin, debugMode, onStatusChange = () => {
    } } = options;
    const [state, subject] = createState({ ...INITIAL_STATE });
    this.state = state;
    this.inner = window;
    this.wizard = this.inner.parent;
    this.allowedOrigin = allowedOrigin || DEFAULT_ALLOWED_ORIGIN;
    this.rootDomElement = rootSelector && document.querySelector(rootSelector) || document.body;
    this.onStatusChange = onStatusChange;
    this.debugMode = debugMode || false;
    subject.subscribe(this.handleStateChange);
    this.initialize().catch((error) => {
      debugLog("[frame-inner] initialize error", this.debugMode, { data: error });
      this.setState({ status: Status.Error, errorCode: error.message || ERROR_INITIALIZE_FAILED });
    });
  }
  emitToWizard = (eventName, data) => {
    debugLog("[frame-inner] emitToWizard parentOrigin", this.debugMode, { data: this.parentOrigin });
    if (!this.parentOrigin) return;
    debugLog("[frame-inner] emitToWizard post message", this.debugMode, {
      data: createFrameInnerEvent(eventName, data),
      parentOrigin: this.parentOrigin
    });
    this.wizard.postMessage(createFrameInnerEvent(eventName, data), this.parentOrigin);
  };
  unload = () => {
    this.setState({ ...INITIAL_STATE });
    this.inner.removeEventListener("message", this.dispatcher);
    this.resizeObserver?.disconnect();
    this.clearListeners();
  };
  initialize = async () => {
    debugLog("[frame-inner] initialize start", this.debugMode);
    const isInitialized = !UNINITIALIZED_STATUSES.has(this.state.status);
    if (isInitialized) return;
    this.setState({ status: Status.Loading });
    this.initEventListeners();
    await this.handshake();
    this.setState({ status: Status.Ready });
    this.subscribeToSizeChange();
  };
  initEventListeners = () => {
    this.inner.addEventListener("message", this.dispatcher);
  };
  handshake = async () => {
    debugLog("[frame-inner] before event handshake:start", this.debugMode);
    await this.once(HANDSHAKE_START_EVENT);
    debugLog("[frame-inner] after event handshake:start", this.debugMode);
    this.emitToWizard(HANDSHAKE_REPLY_EVENT);
    debugLog("[frame-inner] after emitToWizard handshake:reply", this.debugMode);
  };
  dispatcher = (event) => {
    debugLog("[frame-inner] dispatcher", this.debugMode, { event, parentOrigin: this.parentOrigin });
    if (!this.parentOrigin && isHandshakeEvent(event)) {
      this.parentOrigin = event.origin;
    }
    if (!this.parentOrigin) return;
    debugLog("[frame-inner] dispatcher isValidEvent", this.debugMode, {
      isValidEvent: isValidEvent(event, this.allowedOrigin)
    });
    if (!isValidEvent(event, this.allowedOrigin)) {
      return;
    }
    if (event.data.kind === WIZARD_EVENT_KIND) {
      const { eventName, data } = event.data;
      this.emit(eventName, data).catch((error) => {
        debugLog("[frame-inner] dispatcher emit error", this.debugMode, { error });
        this.setState({ status: Status.Error, errorCode: ERROR_EMIT_FAILED });
      });
    }
  };
  setState = (state) => {
    Object.assign(this.state, state);
  };
  subscribeToSizeChange = () => {
    this.resizeObserver = new ResizeObserver((entries) => {
      const [rootNode] = entries;
      const targetElement = rootNode?.target;
      if (!targetElement) return;
      this.updateFrameInnerHeight();
    });
    this.resizeObserver.observe(this.rootDomElement);
  };
  updateFrameInnerHeight = () => {
    const { height: rootHeight } = this.rootDomElement.getBoundingClientRect();
    const documentPaddingTop = getSerializedCssSizeValue(document.body, "padding-top");
    const documentPaddingBottom = getSerializedCssSizeValue(document.body, "padding-bottom");
    const verticalPadding = documentPaddingTop + documentPaddingBottom;
    const height = rootHeight + verticalPadding;
    if (height === this.state.height) return;
    this.setState({ height });
  };
  handleStateChange = ({ name }) => {
    switch (name) {
      case "status": {
        this.onStatusChange(this.state.status);
        break;
      }
      case "errorCode": {
        console.error(this.state.errorCode);
        break;
      }
      case "height": {
        this.emitToWizard(INNER_ROOT_SET_HEIGHT_EVENT, { height: this.state.height });
        break;
      }
    }
  };
}

export { FrameInner };
